// istanbul ignore file
import React, { useEffect } from 'react'
import BackButton from '../../../../components/BackButton'
import {
  Card,
  CardContent,
  CircularProgress,
  Grid,
  FormHelperText,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Link
} from '@mui/material'
import { GenXDropdown, type Option } from '../../../../components/GenXDropdown'
import { useLocation, useNavigate } from 'react-router-dom'
import { type RootState } from '../../../../store'
import { type ConnectedProps, connect } from 'react-redux'
import AuthError from '../../../../components/ErrorComponents/AuthError'
import { Button } from '../../../../components/Button'
import Modal from '../../../../components/Modal'
import LanguageStrings from '../../../../i18n/locales'
import KpiTab from './KpiTab'
import { type AssetDeviceState } from '../../redux/assetListSlice'
import { type KpiAssetState } from '../../redux/kpiAssetListSlice'
import {
  editCustomKpiDetails,
  fetchAssetDeviceList,
  fetchKpiDetails,
  addAssetKpiAlarm,
  fetchKpiConfigs,
  fetchAssetList
} from '../../redux/actionCreators'
import {
  fetchFrequencyMetaData
} from '../../../device-management/redux/actionCreators'
import { useSelector, useDispatch } from 'react-redux'
import {
  reset,
  setTotalizer,
  setTrueCondition,
  setScaleFactor,
  setTotalizerType,
  setTotalizePeriod
} from '../../redux/customKpiSlice'
import { type KpiState } from '../../redux/customKpiDetailsSlice'
import { type EditKpiState } from '../../redux/editCustomKpiSlice'
import { type EditCustomKpiRequest } from '../../services/editCustomKpiService'
import HandleAPIFailures from '../HandleAPIFailures'
import { APIConfiguration } from '../../../../utils/apiConfiguration'
import useCleanup from '../useCleanup'
import Switch, { type SwitchProps } from '@mui/material/Switch'
import { styled } from '@mui/material/styles'
import CustomToast from '../ToastComponent'
import './../ConfigureAlarm.css'
import { assetAlarmReset } from '../../redux/addAssetKpiAlarmSlice'
import { createKpiReset } from '../../redux/createCustomKpiSlice'
import { updateKpiReset } from '../../redux/editCustomKpiSlice'
import { type AssetAlarmState } from '../../redux/addAssetKpiAlarmSlice'
import { type KpiConfigState } from '../../redux/deviceKpiParameterSlice'
import { type FrequencyMetaDataState } from '../../../device-management/redux/frequencyMetaDataSlice'
import {
  isValidKpiData,
  hasSpecialCharAtStartOrEnd,
  sanitizeInput
} from '../../../../utils/validator'
import {
  MAX_KPI_NAME_LENGTH,
  MAX_KPI_REMARK_LENGTH
} from '../../../../utils/KpiConstants'
import { useAuth } from 'react-oidc-context'
import { type OperationPermissionState } from '../../../dashboard/redux/operationPermissionSlice'
import { getOperationPermissions } from '../../../dashboard/redux/actionCreator'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ConfigureDevice from '../../../device-management/components/configureDevice'
import { containerColumns, elementColumns } from '../../../../utils/GlobalStyles'

const KPIDetailStrings = LanguageStrings().ThermalIQStrings.KpiDetailsComponent
const CreateCustomKPIStrings =
  LanguageStrings().ThermalIQStrings.CreateCustomKpiComponent
const EditCustomKPIStrings = LanguageStrings().ThermalIQStrings.EditCustomKpi
const KpiTypeStrings = LanguageStrings().ThermalIQStrings.KpiTypeComponent

const CustomSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName='.Mui-focusVisible' disableRipple {...props} />
))(({ theme }) => ({
  width: 48,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: '5px 9px',
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#64C3FF',
        opacity: 1,
        border: 0
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5
      },
      '& .MuiSwitch-thumb': {
        color: '#000'
      }
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff'
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: '#707070'
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7
    }
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 15,
    height: 15
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#B0B0B0',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500
    })
  }
}))

const alarmArr = [
  {
    kpiDefinitionId: '999',
    kpiName: '',
    assetId: '',
    isKpiEnabled: true,
    isAlarmApplicable: true,
    isAlarmEnabled: false,
    isCustomKpi: true,
    tenantId: 'honeywell',
    unit: null,
    isDefaultConfig: false,
    executionFrequencyMinutes: null,
    isTotalizer: false,
    totalizerType: null,
    alarmLowerLimit: null,
    alarmLowerLimitStateText: null,
    alarmLowerLimitTextColorId: null,
    alarmLowerLimitColorName: null,
    alarmLowerLimitColorValue: null,
    alarmLowerLimitSeverityId: null,
    alarmLowerLimitSeverityLevel: null,
    alarmLowerLimitSeverity: null,
    alarmLowerLimitLabelId: null,
    alarmLowerLimitLabelName: null,
    alarmLowerLimitRecommendationText: null,
    alarmHigherLimit: null,
    alarmHigherLimitStateText: null,
    alarmHigherLimitTextColorId: null,
    alarmHigherLimitColorName: null,
    alarmHigherLimitColorValue: null,
    alarmHigherLimitSeverityId: null,
    alarmHigherLimitSeverityLevel: null,
    alarmHigherLimitSeverity: null,
    alarmHigherLimitLabelId: null,
    alarmHigherLimitLabelName: null,
    alarmHigherLimitRecommendationText: null
  }
]
interface LabelValue {
  label: string
  value: string
  typeId?: string
}

const EditCustomKpi = (props: PropsFromRedux): JSX.Element => {
  useCleanup()
  const DashboardStrings = LanguageStrings().Dashboard
  const [showConfirmation, setShowConfirmation] = React.useState(false)
  const [showCreatePopup, setShowCreatePopup] = React.useState(false)
  const [name, setName] = React.useState('')
  const [remarks, setRemarks] = React.useState('')
  const [selectedSiteId, setSelectedSiteId] = React.useState('')
  const [selectedAssetId, setSelectedAssetId] = React.useState('')
  const [nameErrorInit, setNameErrorInit] = React.useState(false)
  const [remarksErrorInit, setRemarksErrorInit] = React.useState(false)
  const [deviceType, setDeviceType] = React.useState('')
  const [unitErrorInit, setUnitErrorInit] = React.useState(false)
  const [unit, setUnit] = React.useState('')
  const [kpiType, setKpiType] = React.useState('')
  const [enabled, setEnabled] = React.useState(false)
  const [frequency, setFrequency] = React.useState('')
  const [dashboardText, setDashboardText] = React.useState('')
  const [dashboardTextErrorInit, setDashboardTextErrorInit] =
    React.useState(false)
  const customKpiState = useSelector((state: RootState) => state.customKpi)
  const frequencyMetaDataState = useSelector((state: RootState) => state.frequencyMetaDataState)
  const [isTotalizer, setIsTotalizer] = React.useState(customKpiState.totalizer)
  const [isAlarm, setIsAlarm] = React.useState(false)
  const [showAlarmPopup, setShowAlarmPopup] = React.useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Create frequency options from API or use default values
  const frequencyOptions = React.useMemo(() => {
    if (frequencyMetaDataState?.data?.kpiFrequencies != null && frequencyMetaDataState.data.kpiFrequencies.length > 0) {
      return frequencyMetaDataState.data.kpiFrequencies.map(option => ({
        label: option.label,
        value: option.value
      }))
    }
    return []
  }, [frequencyMetaDataState.data?.kpiFrequencies])
  const location = useLocation()
  const defaultAssetTypeId = 'fdd42da4-4a13-4f57-9e1a-208d95c7f215'
  const [assetIndicators, setAssetIndicators] = React.useState<any>([])
  const [selectedAssetTypeId, setSelectedAssetTypeId] = React.useState('')
  const [error, setError] = React.useState<boolean>(false)
  const [asset, setAsset] = React.useState<LabelValue | null>(null)
  const operationPermissions: OperationPermissionState = useSelector(
    (state: RootState) => state.operationPermissions
  )
  const auth = useAuth()

  useEffect(() => {
    setIsTotalizer(customKpiState.totalizer)
  }, [customKpiState.totalizer])

  useEffect(() => {
    if (
      props.kpiAssetList.assets !== null &&
      props.kpiAssetList.assets.length > 0
    ) {
      const selectedAsset = props.kpiAssetList.assets.find(
        (asset) => asset.id === selectedAssetId
      )
      const assetTypesId =
        selectedAsset != null ? selectedAsset.assetType : defaultAssetTypeId
      setSelectedAssetTypeId(assetTypesId ?? '')
      props.fetchKpiConfigs(selectedAssetId, assetTypesId ?? '')
      if (selectedAsset != null) {
        setAsset({
          label: selectedAsset.name,
          value: selectedAsset.id,
          typeId: selectedAsset.assetType
        })
      }
    }
  }, [props.kpiAssetList])

  const setIndicators = (): any => {
    if (
      props.kpiConfigurations.configs !== null &&
      props.kpiConfigurations.configs.length > 0
    ) {
      const selectedAlarm = props.kpiConfigurations.configs.filter(
        (kpi) => kpi.kpiDefinitionId === location.state?.kpiDefinitionId
      )
      if (selectedAlarm.length > 0) {
        setIsAlarm(selectedAlarm[0].isAlarmEnabled)
        setAssetIndicators(selectedAlarm)
      } else {
        const updatedArr = alarmArr.map((indicator: any, index: any) =>
          index === 0
            ? {
                ...indicator,
                kpiDefinitionId: location.state?.kpiDefinitionId
              }
            : indicator
        )
        setAssetIndicators(updatedArr)
      }
    }
  }
  useEffect(() => {
    setIndicators()
  }, [props.kpiConfigurations])

  useEffect(() => {
    if (
      !props.customKpiDetails.isLoading &&
      props.customKpiDetails.kpi != null
    ) {
      setKpiType(
        `${
          props.customKpiDetails.kpi?.is_assetLevel
            ? 'Asset Level Custom KPI'
            : 'Device Level Custom KPI'
        }`
      )
      setName(`${props.customKpiDetails.kpi?.kpi_name ?? ''}`)
      setRemarks(`${props.customKpiDetails.kpi?.kpi_description ?? ''}`)
      setUnit(`${props.customKpiDetails.kpi?.unit ?? ''}`)
      setEnabled(props.customKpiDetails.kpi?.is_enabled ?? false)
      setFrequency(`${props.customKpiDetails.kpi?.frequency ?? ''}`)
      setDashboardText(
        props.customKpiDetails.kpi?.dashboard_text != null
          ? JSON.stringify(props.customKpiDetails.kpi.dashboard_text)
            .replace(/^{|}$/g, '')
            .replace(/"/g, '"')
          : ''
      )
      setIsTotalizer(
        props.customKpiDetails.kpi?.stage[0].operator_type === 'TOTALIZER'
      )
      if (props.customKpiDetails.kpi?.stage[0].operator_type === 'TOTALIZER') {
        dispatch(
          setTotalizer(
            props.customKpiDetails.kpi?.stage[0].operator_type === 'TOTALIZER'
          )
        )
        dispatch(setScaleFactor(props.customKpiDetails.kpi?.scale_factor ?? ''))
        dispatch(
          setTotalizerType(props.customKpiDetails.kpi?.totalizer_type ?? '')
        )
        dispatch(
          setTotalizePeriod(props.customKpiDetails.kpi?.totalize_period ?? '')
        )
        dispatch(
          setTrueCondition(
            props.customKpiDetails.kpi?.stage[0].kpi_parameters[0]
              .parameter_type !== 'NO_CONDITION'
          )
        )
      }
      const siteId =
        props.customKpiDetails.kpi?.stage[0].kpi_parameters[0].site_id ?? ''
      const assetId =
        props.customKpiDetails.kpi?.stage[0].kpi_parameters[0].asset_id ?? ''
      setSelectedSiteId(siteId)
      setSelectedAssetId(assetId)
      if (props.customKpiDetails.kpi?.is_assetLevel) {
        props.fetchAssetList(siteId)
        props.fetchAssetDeviceList(siteId, assetId)
        props.fetchFrequencyMetaData()
      } else {
        setDeviceType(props.customKpiDetails.kpi?.device_type_id ?? '')
      }
    }
  }, [props.customKpiDetails.kpi])

  useEffect(() => {
    !operationPermissions.updated &&
      props.getOperationPermissions(auth.user?.profile.sub as string)
    props.fetchKpiDetails(location.state?.kpiDefinitionId)
    return () => {
      dispatch(reset())
      dispatch(assetAlarmReset())
      dispatch(createKpiReset())
      dispatch(updateKpiReset())
    }
  }, [])

  const checkForIncompleteForm = (): boolean => {
    return (
      isKpiNameNotValid() ||
      isKpiRemarkNotValid() ||
      error ||
      (isAlarm && assetIndicators?.[0] !== undefined &&
        ((assetIndicators[0].alarmLowerLimit === null ||
          assetIndicators[0].alarmLowerLimit === '')) &&
        ((assetIndicators[0].alarmHigherLimit === null ||
          assetIndicators[0].alarmHigherLimit === ''))) ||
      (isAlarm && assetIndicators?.[0] !== undefined &&
        ((assetIndicators[0].lowerLimitOperator === null ||
          assetIndicators[0].lowerLimitOperator === '')) &&
        ((assetIndicators[0].higherLimitOperator === null ||
          assetIndicators[0].higherLimitOperator === ''))) ||
      (kpiType === 'Asset Level Custom KPI' &&
        !customKpiState.totalizer &&
        frequency === '') ||
      (kpiType === 'Asset Level Custom KPI' &&
        !customKpiState.totalizer &&
        isFrequencyNotValid()) ||
      isDashboardTextNotValid() ||
      (!customKpiState.totalizer &&
        customKpiState.stages[0].parameter1.parameter === '' &&
        customKpiState.stages[0].parameter1.constantValue === '') ||
      (!customKpiState.totalizer &&
        customKpiState.stages[0].parameter2.parameter === '' &&
        customKpiState.stages[0].parameter2.constantValue === '') ||
      (!customKpiState.totalizer &&
        customKpiState.stages[0].arithmeticIndicator.value === '') ||
      (customKpiState.totalizer &&
        (customKpiState.stages[0].parameter2.parameter === '' ||
          isScaleNotValid() ||
          customKpiState.totalizerType === '' ||
          customKpiState.totalizePeriod === '')) ||
      (customKpiState.totalizer &&
        customKpiState.trueCondition &&
        (customKpiState.stages[0].parameter1.parameter === null ||
          customKpiState.stages[0].parameter1.parameter === '' ||
          customKpiState.stages[0].parameter2.parameter === ''))
    )
  }

  // istanbul ignore next
  const onCustomKpiEditConfirmClose = (): void => {
    // istanbul ignore next
    setShowConfirmation(false)
  }
  const isValidFrequency = (value: string): boolean => {
    const regexValidator = /^(?!0$)\d{1,500}$/
    return regexValidator.test(value)
  }
  const isValidUnit = (value: string): boolean => {
    // Regex explanation:
    // ^[^\s].{0,28}[^\s]$  - Matches strings of length 2-30:
    //   ^           - Start of string
    //   [^\s]       - First character is not whitespace
    //   .{0,28}     - Followed by 0 to 28 of any character (so total length is 2-30)
    //   [^\s]       - Last character is not whitespace
    //   $           - End of string
    // |             - OR
    // ^[^\s]$       - Matches a single non-whitespace character (length 1)
    // This allows 1-30 characters, no leading/trailing whitespace, internal spaces allowed.
    const regexValidator = /^[^\s].{0,28}[^\s]$|^[^\s]$/
    return regexValidator.test(value)
  }
  const isFrequencyNotValid = (): boolean => {
    return !isValidFrequency(frequency)
  }
  const isUnitNotValid = (): boolean => {
    if (unit.trim() === '') {
      return false // Empty unit is now valid (optional field)
    }
    return !isValidUnit(unit) || hasSpecialCharAtStartOrEnd(unit)
  }
  const isValidScale = (value: string): boolean => {
    const regexValidator = /^(?:0|[1-9]\d*)(?:\.\d+)?$/
    return regexValidator.test(value)
  }
  const isScaleNotValid = (): boolean => {
    return !isValidScale(customKpiState.scaleFactor)
  }
  const kpiNameErrorMessage = (): string | null => {
    if (name.length > MAX_KPI_NAME_LENGTH) {
      return KPIDetailStrings.KpiNameLengthError
    } else if (hasSpecialCharAtStartOrEnd(name)) {
      return KPIDetailStrings.NotAllowedError
    } else if (!isValidKpiData(name)) {
      return KPIDetailStrings.KpiNameErrorMsg
    }
    return null
  }
  const kpiRemarkErrorMessage = (): string | null => {
    if (remarks?.length > MAX_KPI_REMARK_LENGTH) {
      return KPIDetailStrings.KpiRemarksLengthError
    } else if (hasSpecialCharAtStartOrEnd(remarks)) {
      return KPIDetailStrings.NotAllowedError
    } else if (!isValidKpiData(remarks)) {
      return KPIDetailStrings.KpiRemarkErrorMsg
    }
    return null
  }
  const isKpiNameNotValid = (): boolean => {
    return !isValidKpiData(name) || name.length > MAX_KPI_NAME_LENGTH
  }
  const isKpiRemarkNotValid = (): boolean => {
    return !isValidKpiData(remarks) || remarks.length > MAX_KPI_REMARK_LENGTH
  }

  // Helper to convert dashboardText string to object
  const parseDashboardText = (input: string): Record<string, string> => {
    // Add curly braces if not present
    let jsonString = input.trim()
    if (!jsonString.startsWith('{')) {
      jsonString = `{${jsonString}}`
    }
    try {
      // Replace single quotes with double quotes if needed
      jsonString = jsonString.replace(/([a-zA-Z0-9_]+)\s*:/g, '"$1":')
      return JSON.parse(jsonString)
    } catch {
      return {}
    }
  }

  const isDashboardTextNotValid = (): boolean => {
    // Matches: "0": "Text", "-1": "Text", "1.5": "Text", ...
    const regexValidator = /^(\s*"([+-]?\d+(\.\d+)?)"\s*:\s*"(.*?)"\s*,?)*\s*$/
    const trimmed = dashboardText.trim()
    // Also check length and special char at start/end
    if (!regexValidator.test(trimmed) || hasSpecialCharAtStartOrEnd(trimmed)) {
      return true
    }
    // Try to parse as JSON after applying the same transformation as parseDashboardText
    let jsonString = trimmed
    if (!jsonString.startsWith('{')) {
      jsonString = `{${jsonString}}`
    }
    // Replace unquoted keys with quoted keys
    jsonString = jsonString.replace(/([a-zA-Z0-9_]+)\s*:/g, '"$1":')
    try {
      const parsed = JSON.parse(jsonString)
      // Check for any empty value (e.g., "23":"")
      for (const key in parsed) {
        if (typeof parsed[key] !== 'string' || parsed[key].trim() === '') {
          return true
        }
      }
      return false
    } catch {
      return true
    }
  }

  const editCustomKpi = (): void => {
    const editCustomKpiRequest = {
      kpiName: name,
      isAssetLevel: kpiType === 'Asset Level Custom KPI' ? 1 : 0,
      kpiDescription: remarks,
      higherLimitNotification: 'high',
      lowerLimitNotification: 'low',
      units: unit.trim(),
      isAlarmEnabled: isAlarm,
      isEnabled: kpiType === 'Asset Level Custom KPI' ? enabled : null,
      frequency:
        customKpiState.totalizer || frequency === '' ? 1 : parseInt(frequency),
      dashboardText: parseDashboardText(dashboardText),
      scaleFactor: customKpiState.totalizer ? customKpiState.scaleFactor : null,
      totalizerType: customKpiState.totalizer
        ? customKpiState.totalizerType
        : null,
      totalizePeriod: customKpiState.totalizer
        ? customKpiState.totalizePeriod
        : null,
      stage: customKpiState.stages.reduce((acc, stage, index) => {
        if (
          stage.parameter1.parameter === '' &&
          stage.parameter1.constantValue === '' &&
          stage.parameter2.parameter === '' &&
          stage.parameter2.constantValue === ''
        ) {
          return acc
        }
        const kpiParameters = []
        // For index 0, include both parameter1 and parameter2
        if (index === 0) {
          if (
            stage.parameter1.parameter !== '' ||
            stage.parameter1.constantValue !== ''
          ) {
            kpiParameters.push({
              siteId: selectedSiteId,
              assetId: selectedAssetId,
              deviceId: stage.parameter1.deviceId,
              parameterType:
                customKpiState.totalizer && !customKpiState.trueCondition
                  ? 'NO_CONDITION'
                  : stage.parameter1.parameterType,
              parameterId:
                customKpiState.totalizer && !customKpiState.trueCondition
                  ? ''
                  : stage.parameter1.parameterId,
              isDeviceId:
                kpiType === 'Asset Level Custom KPI' &&
                customKpiState.totalizer &&
                !customKpiState.trueCondition
                  ? false
                  : stage.parameter1.constantValue === '' ||
                    stage.parameter1.constantValue === undefined,
              constantValue: stage.parameter1.constantValue
            })
          }
          if (
            stage.parameter2.parameter !== '' ||
            stage.parameter2.constantValue !== ''
          ) {
            kpiParameters.push({
              siteId: selectedSiteId,
              assetId: selectedAssetId,
              deviceId: stage.parameter2.deviceId,
              parameterType: stage.parameter2.parameterType,
              parameterId: stage.parameter2.parameterId,
              isDeviceId:
                stage.parameter2.constantValue === '' ||
                stage.parameter2.constantValue === undefined,
              constantValue: stage.parameter2.constantValue
            })
          }
        } else {
          if (
            !customKpiState.totalizer &&
            (stage.parameter2.parameter !== '' ||
              stage.parameter2.constantValue !== '')
          ) {
            kpiParameters.push({
              siteId: selectedSiteId,
              assetId: selectedAssetId,
              deviceId: stage.parameter2.deviceId,
              parameterType: stage.parameter2.parameterType,
              parameterId: stage.parameter2.parameterId,
              isDeviceId:
                stage.parameter2.constantValue === '' ||
                stage.parameter2.constantValue === undefined,
              constantValue: stage.parameter2.constantValue
            })
          }
        }
        // If no parameters were added, ignore this stage
        if (kpiParameters.length === 0) {
          return acc
        }
        acc.push({
          stageId: index + 1,
          operator: customKpiState.totalizer
            ? 'TOTALIZER'
            : stage.arithmeticIndicator.value,
          operatorType: customKpiState.totalizer
            ? 'TOTALIZER'
            : stage.operatorType,
          calculatedName: stage.calculatedName,
          kpiParameters
        })
        return acc
      }, []) // Reduce to only valid stages
    }
    props.editCustomKpiDetails(
      editCustomKpiRequest,
      location.state?.kpiDefinitionId
    )
  }

  React.useEffect(() => {
    if (
      props.editCustomKpi.data !== undefined &&
      props.editCustomKpi.data != null &&
      isAlarm &&
      enabled
    ) {
      createAlarm()
      setShowCreatePopup(false)
      setShowAlarmPopup(true)
    }
  }, [props.editCustomKpi])

  const createAlarm = (): void => {
    const updatedIndicatorArr = assetIndicators.map(
      (indicator: any, index: any) =>
        index === 0
          ? {
              ...indicator,
              kpiDefinitionId: location.state?.kpiDefinitionId,
              kpiName: name,
              assetId: selectedAssetId,
              isKpiEnabled:
                kpiType === 'Asset Level Custom KPI' ? enabled : false,
              isAlarmApplicable: true,
              isAlarmEnabled: true,
              unit: unit.trim(),
              executionFrequencyMinutes:
                customKpiState.totalizer || frequency === ''
                  ? 1
                  : parseInt(frequency),
              isTotalizer: customKpiState.totalizer,
              totalizerType: customKpiState.totalizer
                ? customKpiState.totalizerType
                : null
            }
          : indicator
    )
    const indicatorPostData = {
      assetTypeId: selectedAssetTypeId,
      assetKpiConfigs: updatedIndicatorArr
    }
    props.addAssetKpiAlarm(indicatorPostData, selectedAssetId)
  }

  const handleAlarmSelect = (
    id: string | number,
    propertyName: string,
    value: string
  ): void => {
    const updatedIndicators = assetIndicators.map((indicator: any) =>
      indicator.kpiDefinitionId === location.state?.kpiDefinitionId
        ? { ...indicator, [propertyName]: value }
        : indicator
    )
    setAssetIndicators([...updatedIndicators])
  }
  const handleChangeTextBox = (
    id: string | number,
    propertyName: string,
    value: string | number
  ): void => {
    const parsedValue: number | string = value
    if (
      propertyName === 'alarmLowerLimit' ||
      propertyName === 'alarmHigherLimit'
    ) {
      if (typeof value === 'number') {
        const minValue = -2147483648
        const maxValue = 2147483647
        if (value < minValue || value > maxValue) {
          setError(true)
          CustomToast({
            type: 'error',
            heading: CreateCustomKPIStrings.Error,
            message: `${String(
              CreateCustomKPIStrings.ValueMustBeBetween
            )} ${minValue} and ${maxValue}`
          })
        }
      }
    }
    setError(false)
    const updatedIndicators = assetIndicators.map((indicator: any) =>
      indicator.kpiDefinitionId === location.state?.kpiDefinitionId
        ? { ...indicator, [propertyName]: parsedValue }
        : indicator
    )
    setAssetIndicators([...updatedIndicators])
  }

  const textStyle = {
    root: {
      backgroundColor: '#272727',
      '& .MuiInputBase-input': {
        color: '#F0F0F0',
        backgroundColor: '#404040'
      },
      '& .MuiInputLabel-root': {
        color: '#F0F0F0',
        '&.Mui-focused': {
          color: '#F0F0F0'
        }
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: '#00000000'
        },
        '&:hover fieldset': {
          borderColor: '#00000000'
        },
        '&.Mui-focused fieldset': {
          borderColor: '#00000000'
        }
      }
    }
  }
  const breadcrumbStyle = {
    fontFamily: 'Honeywell Sans Web',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '16px',
    textAlign: 'left',
    cursor: 'pointer'
  }

  const closeAndCleanState = (): void => {
    setShowCreatePopup(false)
    dispatch(reset())
    navigate('/custom-kpi-list')
  }
  const closeAndCleanAlarmState = (): void => {
    setShowAlarmPopup(false)
    dispatch(reset())
    navigate('/custom-kpi-list')
  }

  const handleTotalizerChange = (e: any): void => {
    setIsTotalizer(e.target.checked)
    dispatch(setTotalizer(e.target.checked))
  }

  const handleEditCustomKpiWithFailure = (): JSX.Element => {
    return (
      <>
        <Card
          sx={{
            backgroundColor: '#272727',
            borderRadius: '10px',
            color: '#F0F0F0 !important',
            padding: '10px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'visible'
          }}
        >
          <div style={{ marginLeft: '15px' }}></div>
          <Typography
            sx={{
              marginLeft: '15px',
              fontFamily: 'Honeywell Sans Web',
              fontSize: '16px',
              fontWeight: 600,
              letterSpacing: '2.5px'
            }}
          >
            {KPIDetailStrings.KPIInformation}
          </Typography>
          <CardContent>
            <Grid container spacing={4} alignItems='flex-start' columns={{ ...containerColumns }}>
              <Grid
                item
                { ...elementColumns.oneColumn }
              >
                <Typography
                  sx={{
                    fontFamily: 'Honeywell Sans Web',
                    fontSize: '15px',
                    padding: 0.5,
                    display: 'block'
                  }}
                >
                  {KpiTypeStrings.KpiType}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Honeywell Sans Web',
                    fontSize: '15px',
                    padding: 0.5,
                    display: 'block'
                  }}
                  variant='body1'
                >
                  {kpiType}
                </Typography>
              </Grid>
              <Grid
                item
                { ...elementColumns.oneColumn }
              >
                <Typography
                  sx={{
                    fontFamily: 'Honeywell Sans Web',
                    fontSize: '15px',
                    padding: 0.5,
                    display: 'block'
                  }}
                >
                  {KPIDetailStrings.KpiName}
                  <sup style={{ color: '#B0B0B0' }}> *</sup>
                </Typography>
                <TextField
                  id='name-textfield'
                  variant='outlined'
                  placeholder='Text'
                  InputLabelProps={{
                    disabled: true
                  }}
                  size='small'
                  className='custom-textfield'
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value)
                    if (e.target.value.length === 0) setNameErrorInit(false)
                    else setNameErrorInit(true)
                  }}
                  sx={{
                    ...textStyle.root,
                    width: '100%',
                    border:
                      isKpiNameNotValid() && nameErrorInit
                        ? '1px solid #EE3426'
                        : 'none'
                  }}
                />
                {isKpiNameNotValid() && nameErrorInit && (
                  <FormHelperText
                    sx={{ color: '#EE3426', paddingLeft: '2px' }}
                    className='errorColor'
                  >
                    {kpiNameErrorMessage()}
                  </FormHelperText>
                )}
              </Grid>
              <Grid
                item
                { ...elementColumns.oneColumn }
              >
                <Typography
                  sx={{
                    fontFamily: 'Honeywell Sans Web',
                    fontSize: '15px',
                    padding: 0.5,
                    display: 'block'
                  }}
                >
                  {KPIDetailStrings.Remarks}
                  <sup style={{ color: '#B0B0B0' }}> *</sup>
                </Typography>
                <TextField
                  id='remarks-textfield'
                  variant='outlined'
                  placeholder='Text'
                  InputLabelProps={{
                    disabled: true
                  }}
                  size='small'
                  className='custom-textfield'
                  value={remarks}
                  onChange={(e) => {
                    setRemarks(e.target.value)
                    if (e.target.value.length === 0) setRemarksErrorInit(false)
                    else setRemarksErrorInit(true)
                  }}
                  sx={{
                    ...textStyle.root,
                    width: '100%',
                    border:
                      isKpiRemarkNotValid() && remarksErrorInit
                        ? '1px solid #EE3426'
                        : 'none'
                  }}
                />
                {isKpiRemarkNotValid() && remarksErrorInit && (
                  <FormHelperText
                    sx={{
                      color: '#EE3426',
                      paddingLeft: '10px',
                      whiteSpace: 'nowrap'
                    }}
                    className='errorColor'
                  >
                    {kpiRemarkErrorMessage()}
                  </FormHelperText>
                )}
              </Grid>
              {(kpiType === 'Device Level Custom KPIs' ||
                kpiType === 'Asset Level Custom KPIs') && (
                <Grid
                  item
                  { ...elementColumns.halfColumn }
                  sx={{ xl: { mt: '25px' } }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isTotalizer}
                        onChange={(e) => {
                          handleTotalizerChange(e)
                        }}
                        name='totalizer'
                        color='primary'
                        sx={{
                          '& .MuiSvgIcon-root': {
                            color: isTotalizer ? 'primary.main' : '#505050'
                          }
                        }}
                      />
                    }
                    label='Enable Totalizer'
                  />
                </Grid>
              )}
              {kpiType === 'Asset Level Custom KPI'
                ? (
                <Grid
                  item
                  { ...elementColumns.halfColumn }
                  sx={{ xl: { mt: '25px' } }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enabled}
                        onChange={(e) => {
                          setEnabled(e.target.checked)
                        }}
                        name='enabled'
                        sx={{
                          '& .MuiSvgIcon-root': {
                            color: enabled ? 'primary.main' : '#505050'
                          }
                        }}
                      />
                    }
                    label='Enable'
                  />
                </Grid>
                  )
                : null}
            </Grid>
          </CardContent>
        </Card>
        {
          <>
            <Card
              sx={{
                marginTop: '15px',
                backgroundColor: '#272727',
                borderTopLeftRadius: '10px',
                borderTopRightRadius: '10px',
                color: '#F0F0F0 !important',
                padding: '10px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible'
              }}
            >
              <div style={{ marginLeft: '15px' }}></div>
              <CardContent>
                <Grid container spacing={4} alignItems='flex-start' columns={{ ...containerColumns }}>
                  {kpiType === 'Asset Level Custom KPI'
                    ? (
                    <>
                      <Grid item { ...elementColumns.oneColumn }>
                        <Typography
                          sx={{
                            fontFamily: 'Honeywell Sans Web',
                            fontSize: '15px',
                            padding: 0.5,
                            display: 'block'
                          }}
                        >
                          {EditCustomKPIStrings.Site}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Honeywell Sans Web',
                            fontSize: '15px',
                            padding: 0.5,
                            display: 'block'
                          }}
                          variant='body1'
                        >
                          {
                            props.customKpiDetails.kpi?.stage[0]
                              .kpi_parameters[0].site_name
                          }
                        </Typography>
                      </Grid>
                      <Grid item { ...elementColumns.oneColumn }>
                        <Typography
                          sx={{
                            fontFamily: 'Honeywell Sans Web',
                            fontSize: '15px',
                            padding: 0.5,
                            display: 'block'
                          }}
                        >
                          {EditCustomKPIStrings.Asset}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Honeywell Sans Web',
                            fontSize: '15px',
                            padding: 0.5,
                            display: 'block'
                          }}
                          variant='body1'
                        >
                          {
                            props.customKpiDetails.kpi?.stage[0]
                              .kpi_parameters[0].asset_name
                          }
                        </Typography>
                      </Grid>
                      {!customKpiState.totalizer && (
                        <Grid
                          item
                          { ...elementColumns.oneColumn }
                          sx={{ mt: '-8px' }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Honeywell Sans Web',
                              fontSize: '15px',
                              padding: 0.5,
                              display: 'block'
                            }}
                          >
                            {CreateCustomKPIStrings.Frequency}
                            <sup style={{ color: '#B0B0B0' }}> *</sup>
                          </Typography>
                          <GenXDropdown
                            data-testid="frequency"
                            options={frequencyOptions}
                            value={frequencyOptions.find(option => option.value === frequency) ?? null}
                            onSelect={(selectedOption: Option) => {
                              setFrequency(selectedOption.value)
                            }}
                            placeholder="Select Frequency"
                            isSearchable={false}
                            width={'100%'}
                          />
                        </Grid>
                      )}
                    </>
                      )
                    : (
                    <>
                      <Grid
                        item
                        { ...elementColumns.oneColumn }
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Honeywell Sans Web',
                            fontSize: '15px',
                            padding: 0.5,
                            display: 'block'
                          }}
                        >
                          {CreateCustomKPIStrings.DeviceType}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Honeywell Sans Web',
                            fontSize: '15px',
                            padding: 0.5,
                            display: 'block'
                          }}
                          variant='body1'
                        >
                          {props.customKpiDetails.kpi?.device_type}
                        </Typography>
                      </Grid>
                    </>
                      )}
                  <Grid
                    item
                    { ...elementColumns.oneColumn }
                    sx={{ mt: '-8px' }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Honeywell Sans Web',
                        fontSize: '15px',
                        padding: 0.5,
                        display: 'block'
                      }}
                    >
                      {CreateCustomKPIStrings.Unit}
                    </Typography>
                    <TextField
                      id='remarks-textfield'
                      variant='outlined'
                      placeholder='Text'
                      InputLabelProps={{
                        disabled: true
                      }}
                      size='small'
                      className='custom-textfield'
                      value={unit}
                      onChange={(e) => {
                        // Allow internal spaces but prevent leading/trailing spaces
                        const sanitizedValue = sanitizeInput(e.target.value, 30)
                        setUnit(sanitizedValue)
                        setUnitErrorInit(sanitizedValue.length > 0)
                      }}
                      sx={{
                        ...textStyle.root,
                        width: '100%',
                        border:
                          isUnitNotValid() && unitErrorInit
                            ? '1px solid #EE3426'
                            : 'none'
                      }}
                    />
                    {isUnitNotValid() && unitErrorInit && (
                      <FormHelperText
                        sx={{
                          color: '#EE3426',
                          paddingLeft: '10px',
                          whiteSpace: 'nowrap'
                        }}
                        className='errorColor'
                      >
                        {CreateCustomKPIStrings.UnitValidationMessage}
                      </FormHelperText>
                    )}
                  </Grid>
                  {!customKpiState.totalizer && (
                    <Grid
                      item
                      { ...elementColumns.oneColumn }
                      sx={{ mt: '-8px' }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Honeywell Sans Web',
                          fontSize: '15px',
                          padding: 0.5,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        {KPIDetailStrings.DashboardText}
                        <Tooltip
                          title={
                            <span
                              style={{
                                minWidth: 1300,
                                display: 'inline-block',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word'
                              }}
                            >
                              {KPIDetailStrings.DashboardTextFormat}
                              <br />
                              e.g. &quot;0&quot;:
                              &quot;Initiate&quot;,&quot;2&quot;:
                              &quot;Standby&quot;, &quot;4&quot;:
                              &quot;Purge&quot;, &quot;8&quot;: &quot;Pilot
                              Ignition&quot;
                            </span>
                          }
                          arrow
                          placement='top'
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: '#F0F0F0',
                                color: '#303030',
                                fontSize: '14px',
                                boxShadow: 2,
                                borderRadius: '6px',
                                maxWidth: 450,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word'
                              }
                            },
                            arrow: {
                              sx: {
                                color: '#F0F0F0'
                              }
                            }
                          }}
                        >
                          <InfoOutlinedIcon
                            sx={{
                              fontSize: 18,
                              color: '#B0B0B0',
                              marginLeft: '4px',
                              cursor: 'pointer'
                            }}
                          />
                        </Tooltip>
                      </Typography>
                      <TextField
                        id='remarks-textfield'
                        variant='outlined'
                        placeholder='“Value” : “Name”'
                        InputLabelProps={{
                          disabled: true
                        }}
                        size='small'
                        className='custom-textfield'
                        value={dashboardText}
                        onChange={(e) => {
                          setDashboardText(e.target.value)
                          if (e.target.value.length === 0) { setDashboardTextErrorInit(false) } else setDashboardTextErrorInit(true)
                        }}
                        multiline
                        rows={1}
                        sx={{
                          ...textStyle.root,
                          width: '100%',
                          border:
                            isDashboardTextNotValid() && dashboardTextErrorInit
                              ? '1px solid #EE3426'
                              : 'none',
                          '& .MuiInputBase-root': {
                            padding: 0,
                            borderRadius: '4px'
                          },
                          '& .MuiInputBase-input': {
                            color: '#F0F0F0',
                            backgroundColor: '#404040',
                            padding: '8px 12px',
                            borderRadius: '4px'
                          },
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '4px',
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#404040',
                              borderWidth: '1px'
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#404040'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#404040'
                            }
                          }
                        }}
                      />
                      {isDashboardTextNotValid() && dashboardTextErrorInit && (
                        <FormHelperText
                          sx={{
                            color: '#EE3426',
                            paddingLeft: '10px',
                            whiteSpace: 'nowrap'
                          }}
                          className='errorColor'
                        >
                          {KPIDetailStrings.DashboardTextValidation}
                        </FormHelperText>
                      )}
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
            {props.assetDeviceList.devices.length > 0 ||
            kpiType === 'Device Level Custom KPI'
              ? (
              <Card
                sx={{
                  marginTop: '2px',
                  borderBottomLeftRadius: '10px',
                  borderBottomRightRadius: '10px',
                  backgroundColor: '#272727',
                  color: '#F0F0F0 !important',
                  padding: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'visible',
                  overflowY: 'auto',
                  flex: 1
                }}
              >
                <KpiTab
                  selectedAsset={asset}
                  assetList={props.assetDeviceList.devices}
                  kpiType={kpiType}
                  deviceType={deviceType}
                  currentKpiId={location.state?.kpiDefinitionId}
                />
              </Card>
                )
              : (
              <HandleAPIFailures
                response={props.assetDeviceList}
                handleClose={() => {
                  navigate('/custom-kpi-list')
                }}
                title={'Devices'}
              />
                )}
            {operationPermissions.permissions.can_modify_tiq_configurations
              ? (
                  kpiType === 'Asset Level Custom KPI' &&
              enabled && (
                <Card
                  sx={{
                    marginTop: '15px',
                    borderRadius: '10px',
                    backgroundColor: '#272727',
                    color: '#F0F0F0 !important',
                    padding: '10px 20px 30px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'visible'
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Honeywell Sans Web',
                      fontSize: '16px',
                      fontWeight: 800,
                      letterSpacing: '2.5px'
                    }}
                  >
                    {CreateCustomKPIStrings.CONFIGURE_ALARM}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Honeywell Sans Web',
                      color: '#B0B0B0',
                      fontSize: '16px',
                      fontWeight: 500
                    }}
                  >
                    {CreateCustomKPIStrings.Alarm_Note}
                  </Typography>
                  <Grid container spacing={2} alignItems='flex-start'>
                    <Grid
                      item
                      xs={12}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Honeywell Sans Web',
                          marginRight: '12px',
                          fontSize: '16px',
                          fontWeight: 500
                        }}
                      >
                        {CreateCustomKPIStrings.EnableAlarm}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Honeywell Sans Web',
                          fontSize: '15px',
                          padding: 0.5,
                          display: 'block'
                        }}
                      >
                        <FormControlLabel
                          control={
                            <CustomSwitch
                              sx={{ m: 1 }}
                              onChange={() => {
                                setIsAlarm(!isAlarm)
                              }}
                              checked={isAlarm}
                              size='medium'
                              data-testid='switch-alarm'
                            />
                          }
                          style={{ marginTop: '8px' }}
                          label=''
                        />
                      </Typography>
                      {isAlarm && (
                        <ConfigureDevice
                          isEdit={false}
                          isAlarmEnabledCompo={false}
                          deviceIndicators={assetIndicators[0]}
                          handleChangeTextBox={handleChangeTextBox}
                          alarmSelect={handleAlarmSelect}
                          handleCancel={() => setIndicators()}
                        />
                      )}
                    </Grid>
                  </Grid>
                  {isAlarm && (
                    <div className='error-response'>
                      <span className='error-upload-message'>
                        Please click on “Configure Alarm” to add alarms.
                      </span>
                    </div>
                  )}
                </Card>
                  )
                )
              : (
              <></>
                )}
          </>
        }
      </>
    )
  }

  const handleEditKpiWithFailure = (): JSX.Element => {
    // istanbul ignore else
    if (
      props.editCustomKpi.httpStatus === APIConfiguration.SUCCESS_HTTP_STATUS
    ) {
      return (
        <Modal
          buttonTypeNegative='hollow'
          title='Successfully Updated'
          LabelPositive='Okay'
          onClickPositive={() => {
            navigate('/custom-kpi-list')
          }}
          onClose={() => {
            navigate('/custom-kpi-list')
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Honeywell Sans Web',
              fontSize: '15px',
              paddingTop: '1.2em'
            }}
          >
            {EditCustomKPIStrings.UpdateSuccessMessage}
          </Typography>
        </Modal>
      )
    } else if (props.editCustomKpi.isLoading) {
      return (
        <Modal title='Updating Custom KPI'>
          <div className='CircularprogressAmi'>
            <CircularProgress />
          </div>
        </Modal>
      )
    } else if (
      props.editCustomKpi.httpStatus ===
      APIConfiguration.UNAUTHORIZED_HTTP_STATUS
    ) {
      return (
        <Modal
          titleStyle={{ paddingRight: '2em' }}
          width={'fit-content'}
          height={'fit-content'}
          buttonTypeNegative='hollow'
          LabelNegative='Close'
          onClickNegative={closeAndCleanState}
          onClose={closeAndCleanState}
          title='Updating Custom KPI'
        >
          <div className='authError'>
            <AuthError
              className={'errorCompInsideModal'}
              errorMessage='Unauthorized'
            />
          </div>
        </Modal>
      )
    } else if (
      props.editCustomKpi.httpStatus ===
      APIConfiguration.ACCESS_FORBIDDEN_HTTP_STATUS
    ) {
      return (
        <Modal
          titleStyle={{ paddingRight: '2em' }}
          width={'fit-content'}
          height={'fit-content'}
          buttonTypeNegative='hollow'
          LabelNegative='Close'
          onClickNegative={closeAndCleanState}
          onClose={closeAndCleanState}
          title='Updating Custom KPI'
          onClickPositive={() => {
            editCustomKpi()
          }}
        >
          <div className='authError'>
            <AuthError
              className={'errorCompInsideModal'}
              customError='accessForbidden'
            />
          </div>
        </Modal>
      )
    } else {
      return (
        <Modal
          width={'fit-content'}
          height={'fit-content'}
          buttonTypeNegative='hollow'
          LabelNegative='Close'
          LabelPositive='Retry'
          onClickNegative={closeAndCleanState}
          onClose={closeAndCleanState}
          title='Updating Custom KPI'
        >
          <div className='authError'>
            <AuthError
              className={'errorCompInsideModal'}
              customError={props.editCustomKpi.error}
            />
          </div>
        </Modal>
      )
    }
  }

  const handleUpdateAlarmWithFailure = (): JSX.Element => {
    if (
      props.assetKpiAlarmState.httpStatus ===
      APIConfiguration.CREATED_HTTP_STATUS
    ) {
      return (
        <Modal
          buttonTypeNegative='hollow'
          title='Successfully Updated'
          LabelPositive='Okay'
          onClickPositive={() => {
            navigate('/custom-kpi-list')
          }}
          onClose={() => {
            navigate('/custom-kpi-list')
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Honeywell Sans Web',
              fontSize: '15px',
              paddingTop: '1.2em'
            }}
          >
            {EditCustomKPIStrings.UpdateSuccessMessage}
          </Typography>
        </Modal>
      )
    } else if (props.assetKpiAlarmState.isLoading) {
      return (
        <Modal title='Updating Custom KPI'>
          <div className='CircularprogressAmi'>
            <CircularProgress />
          </div>
        </Modal>
      )
    } else if (
      props.assetKpiAlarmState.httpStatus ===
      APIConfiguration.UNAUTHORIZED_HTTP_STATUS
    ) {
      return (
        <Modal
          titleStyle={{ paddingRight: '2em' }}
          width={'fit-content'}
          height={'fit-content'}
          buttonTypeNegative='hollow'
          LabelNegative='Close'
          onClickNegative={closeAndCleanAlarmState}
          onClose={closeAndCleanAlarmState}
          title='Updating Custom KPI'
        >
          <div className='authError'>
            <AuthError
              className={'errorCompInsideModal'}
              errorMessage='Unauthorized'
            />
          </div>
        </Modal>
      )
    } else if (
      props.assetKpiAlarmState.httpStatus ===
      APIConfiguration.ACCESS_FORBIDDEN_HTTP_STATUS
    ) {
      return (
        <Modal
          titleStyle={{ paddingRight: '2em' }}
          width={'fit-content'}
          height={'fit-content'}
          buttonTypeNegative='hollow'
          LabelNegative='Close'
          onClickNegative={closeAndCleanAlarmState}
          onClose={closeAndCleanAlarmState}
          title='Updating Custom KPI'
          onClickPositive={() => {
            editCustomKpi()
          }}
        >
          <div className='authError'>
            <AuthError
              className={'errorCompInsideModal'}
              errorMessage='accessForbidden'
            />
          </div>
        </Modal>
      )
    } else {
      return (
        <Modal
          width={'fit-content'}
          height={'fit-content'}
          buttonTypeNegative='hollow'
          LabelNegative='Close'
          onClickNegative={closeAndCleanAlarmState}
          onClose={closeAndCleanAlarmState}
          title='Updating Custom KPI'
        >
          <div className='authError'>
            <AuthError
              className={'errorCompInsideModal'}
              customError={props.assetKpiAlarmState.error}
            />
          </div>
        </Modal>
      )
    }
  }

  return props.customKpiDetails.isLoading
    ? (
    <div className='CircularprogressAmi'>
      <CircularProgress />
    </div>
      )
    : props.customKpiDetails.httpStatus ===
    APIConfiguration.SUCCESS_HTTP_STATUS
      ? (
    <div>
      <div
        className='link-breadcrumb'
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <div
          style={{
            marginTop: '6px',
            marginRight: '10px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <BackButton />
        </div>
        <Link aria-label="navBreadcrumb" className="assetmgmtli" data-testid="bc-link-1" underline="none" color="inherit" onClick={() => { navigate('/thermal-dashboard') }}>
          {DashboardStrings.HOME}
        </Link>
        <span style={{ padding: '0 0.5em' }}>/</span>
        <Typography
          data-testid='configurationbreadcrumb'
          onClick={() => {
            navigate('/custom-kpi-list')
          }}
          sx={breadcrumbStyle}
        >
          {EditCustomKPIStrings.KPIConfiguration}
        </Typography>
        <span style={{ padding: '0 0.5em' }}>/</span>
        <Typography sx={breadcrumbStyle}>
          {EditCustomKPIStrings.EditCustomKPI}
        </Typography>
      </div>
      <div className='topUtilityBar'>
        <Typography
          sx={{
            fontFamily: 'Honeywell Sans Web',
            fontSize: '24px',
            fontWeight: 700
          }}
        >
          {EditCustomKPIStrings.EditCustomKPI}
        </Typography>
      </div>
      {handleEditCustomKpiWithFailure()}
      <div
        style={{
          width: '100%',
          height: '80px',
          backgroundColor: '#272727',
          margin: '50px 0 10px 0',
          borderRadius: 10
        }}
      >
        <div
          style={{
            height: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0 1em'
          }}
        >
          <Button
            dataTestId='ConfigCancel'
            type='hollow'
            onClick={() => {
              navigate('/custom-kpi-list')
            }}
          >
            {CreateCustomKPIStrings.Cancel}
          </Button>
          <Button
            dataTestId='ConfigCreate'
            primaryColor={checkForIncompleteForm() ? '#B0B0B0' : undefined}
            onClick={() => {
              setShowConfirmation(true)
            }}
            disabled={checkForIncompleteForm()}
          >
            Update
          </Button>
        </div>
      </div>
      {showConfirmation && (
        <Modal
          buttonTypeNegative='hollow'
          title='Update Custom KPI'
          LabelNegative='Cancel'
          LabelPositive='Update'
          onClickPositive={() => {
            setShowConfirmation(false)
            setShowCreatePopup(true)
            editCustomKpi()
          }}
          onClose={onCustomKpiEditConfirmClose}
          onClickNegative={onCustomKpiEditConfirmClose}
        >
          <Typography
            sx={{
              fontFamily: 'Honeywell Sans Web',
              fontSize: '15px',
              paddingTop: '1.2em'
            }}
          >
            {EditCustomKPIStrings.EditConfirmationMessage}
          </Typography>
        </Modal>
      )}
      {showCreatePopup && handleEditKpiWithFailure()}
      {showAlarmPopup && handleUpdateAlarmWithFailure()}
    </div>
        )
      : (
    <Modal
      width={'fit-content'}
      height={'fit-content'}
      buttonTypeNegative='hollow'
      LabelNegative='Close'
      onClickNegative={() => {
        navigate('/custom-kpi-list')
      }}
      onClose={() => {
        navigate('/custom-kpi-list')
      }}
      title='Fetching KPI Details'
    >
      <div className='authError'>
        <AuthError
          className={'errorCompInsideModal'}
          customError={props.customKpiDetails.error}
        />
      </div>
    </Modal>
        )
}

interface DispatchToProps {
  fetchAssetDeviceList: (siteId: string, assetId?: string) => void
  fetchAssetList: (siteId: string) => void
  fetchKpiDetails: (kpiDefinitionId: string) => void
  editCustomKpiDetails: (
    editKpiRequest: EditCustomKpiRequest,
    kpiDefinitionId: string
  ) => void
  addAssetKpiAlarm: (addAlarmRequest: any, assetId: string) => void
  fetchKpiConfigs: (assetId: string, assetTypeId: string) => void
  getOperationPermissions: (sub: string) => void
  fetchFrequencyMetaData: () => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchAssetDeviceList: (siteId: string, assetId?: string) =>
    dispatch(fetchAssetDeviceList(siteId, assetId)),
  fetchAssetList: (siteId: string) => dispatch(fetchAssetList(siteId)),
  fetchKpiDetails: (kpiDefinitionId: string) =>
    dispatch(fetchKpiDetails(kpiDefinitionId)),
  editCustomKpiDetails: (
    editKpiRequest: EditCustomKpiRequest,
    kpiDefinitionId: string
  ) => dispatch(editCustomKpiDetails(editKpiRequest, kpiDefinitionId)),
  addAssetKpiAlarm: (addAlarmRequest: any, assetId: string) =>
    dispatch(addAssetKpiAlarm(addAlarmRequest, assetId)),
  fetchKpiConfigs: (assetId: string, assetTypeId: string) =>
    dispatch(fetchKpiConfigs(assetId, assetTypeId)),
  getOperationPermissions: (sub: string) =>
    dispatch(getOperationPermissions(sub)),
  fetchFrequencyMetaData: () => dispatch(fetchFrequencyMetaData())
})

interface StateToProps {
  assetDeviceList: AssetDeviceState
  kpiAssetList: KpiAssetState
  customKpiDetails: KpiState
  editCustomKpi: EditKpiState
  assetKpiAlarmState: AssetAlarmState
  kpiConfigurations: KpiConfigState
  frequencyMetaDataState: FrequencyMetaDataState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  assetDeviceList: state.tiqAssetList,
  kpiAssetList: state.kpiAssetList,
  customKpiDetails: state.kpiDetails,
  editCustomKpi: state.editCustomKpi,
  assetKpiAlarmState: state.assetKpiAlarmData,
  kpiConfigurations: state.kpiConfigurations,
  frequencyMetaDataState: state.frequencyMetaDataState
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(EditCustomKpi)
